// context/visibilityContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getConfig } from 'services/org';
import { UserContext } from 'context/userContext';

export const VisibilityContext = createContext();

export const VisibilityProvider = ({ children }) => {
  const { userId } = useContext(UserContext);
  const [visibilityConfig, setVisibilityConfig] = useState({ cpq: [], rc: [] });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true); // Start true on initial load
  const [configError, setConfigError] = useState(null);

  // --- REFACTORED: fetchVisibilityConfig function ---
  const fetchVisibilityConfig = useCallback(async () => {
    // If there's no user, there's no config to fetch. Reset to defaults.
    if (!userId) {
      setVisibilityConfig({ cpq: [], rc: [] });
      setIsLoadingConfig(false);
      setConfigError(null);
      return;
    }

    setIsLoadingConfig(true);
    setConfigError(null);
    try {
      // NEW PATTERN: The service returns the data object directly or throws an error.
      const { config: fetchedConfig } = await getConfig();

      // Use the fetched config if it's a valid object, otherwise default to empty arrays.
      const cpqConfig = Array.isArray(fetchedConfig?.cpq) ? fetchedConfig.cpq : [];
      const rcConfig = Array.isArray(fetchedConfig?.rc) ? fetchedConfig.rc : [];

      setVisibilityConfig({
        cpq: cpqConfig,
        rc: rcConfig,
      });
      console.log('VisibilityContext: Successfully fetched and set config.');
    } catch (error) {
      // The apiClient interceptor has already formatted the error.
      console.error('VisibilityContext: Error fetching visibility configuration:', error.message);
      setConfigError(error.message);
      // On failure, reset to a safe, empty state.
      setVisibilityConfig({ cpq: [], rc: [] });
    } finally {
      setIsLoadingConfig(false);
    }
  }, [userId]); // Dependency is on userId

  // This useEffect will run when the component mounts and whenever `userId` changes.
  useEffect(() => {
    fetchVisibilityConfig();
  }, [fetchVisibilityConfig]); // The dependency is now the stable useCallback function

  // Expose a manual refresh function
  const refreshConfig = () => {
    console.log('VisibilityContext: Manual refresh triggered.');
    fetchVisibilityConfig();
  };

  // This function can be used for optimistic updates if needed
  const updateVisibilityConfig = (newConfig) => {
    if (newConfig && newConfig.cpq && newConfig.rc) {
      setVisibilityConfig({
        cpq: [...newConfig.cpq],
        rc: [...newConfig.rc],
      });
    }
  };

  return (
    <VisibilityContext.Provider
      value={{
        visibilityConfig,
        isLoadingConfig,
        configError,
        fetchVisibilityConfig: refreshConfig,
        updateVisibilityConfig,
      }}
    >
      {children}
    </VisibilityContext.Provider>
  );
};

VisibilityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useVisibility = () => useContext(VisibilityContext);
